import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Alert,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAddUserMutation, useUpdateUserMutation } from "../api/usersApi";
import { User } from "../types/user";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    user?: User | null;
}

const AddEditUser: React.FC<Props> = ({ open, setOpen: setModalOpen, user }) => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [addUser, { isLoading: isAdding }] = useAddUserMutation();
    const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                password: "", // Leave empty unless changing
                confirmPassword: "",
            });
        } else {
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                confirmPassword: "",
            });
        }
    }, [user]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        
        // Clear password error when user starts typing
        if (name === "password" || name === "confirmPassword") {
            setPasswordError(null);
        }
    };

    const validatePasswords = () => {
        if (!user && formData.password !== formData.confirmPassword) {
            setPasswordError("Passwords do not match");
            return false;
        }
        if (user && formData.password && formData.password !== formData.confirmPassword) {
            setPasswordError("Passwords do not match");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validatePasswords()) {
            return;
        }

        try {
            // Only send necessary fields to the API
            const submitData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password
            };

            if (user) {
                await updateUser({ id: user.id, body: submitData }).unwrap();
            } else {
                await addUser({ body: submitData }).unwrap();
            }
            
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                confirmPassword: ""
            });
            setSubmissionError(null);
            setPasswordError(null);
            setModalOpen(false);
        } catch (error: any) {
            setSubmissionError("Failed to create users. Please try again.");
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                confirmPassword: ""
            });
        }
    };

    const handleCloseError = () => {
        setSubmissionError(null);
        setPasswordError(null);
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <Dialog open={open} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>{user ? "Edit User" : "Add New User"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <TextField
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        fullWidth
                        placeholder={user ? "Leave blank to keep current password" : ""}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        fullWidth
                        placeholder={user ? "Leave blank to keep current password" : ""}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle confirm password visibility"
                                        onClick={handleClickShowConfirmPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {passwordError && (
                        <Alert severity="error" onClose={handleCloseError}>
                            {passwordError}
                        </Alert>
                    )}
                    {submissionError && (
                        <Alert severity="error" onClose={handleCloseError}>
                            {submissionError}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {
                    setModalOpen(false);
                    setFormData({
                        firstName: "",
                        lastName: "",
                        email: "",
                        password: "",
                        confirmPassword: "",
                    });
                    setPasswordError(null);
                }} disabled={isAdding || isUpdating}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isAdding || isUpdating}
                >
                    {user ? "Update" : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditUser;
